// ====================================
// COMMON UTILITIES - Export System
// ====================================

export const getFullText = (text: string | null | undefined): string => {
  return text || '-';
};

export const getTimestamp = (): string => {
  return new Date().toLocaleString('pt-PT', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

export const formatTableDisplayName = (tableName: string): string => {
  const tableDisplayNames: Record<string, string> = {
    'AbscenceType': 'Tipos de Ausência',
    'Client': 'Clientes',
    'Component': 'Componentes',
    'DigitalSign': 'Assinaturas Digitais',
    'DigitalSignApprover': 'Aprovadores de Assinatura',
    'Distance': 'Distâncias',
    'Document': 'Documentos',
    'DocumentRequired': 'Documentos Obrigatórios',
    'DocumentType': 'Tipos de Documento',
    'Employee': 'Funcionários',
    'Function': 'Funções',
    'Invoice': 'Faturas',
    'JobTitle': 'Cargos',
    'Parameter': 'Parâmetros',
    'Permission': 'Permissões',
    'Project': 'Projetos',
    'ProjectMember': 'Membros do Projeto',
    'PurchaseOrder': 'Ordens de Compra',
    'Role': 'Papéis',
    'TemplateMail': 'Templates de Email',
    'Timesheet': 'Folhas de Horas',
    'TimesheetDay': 'Dias de Timesheet',
    'TimesheetReport': 'Relatórios de Timesheet',
    'Trip': 'Viagens',
    'Tripsheet': 'Folhas de Viagem',
    'TripsheetReport': 'Relatórios de Viagem',
    'User': 'Usuários',
    'Vacation': 'Férias',
    'Versioning': 'Versionamento',
    'Workflow': 'Fluxo de Trabalho'
  };

  return tableDisplayNames[tableName] || tableName;
};

export const formatColumnDisplayName = (columnName: string, tableName?: string): string => {
  // Mapeamentos específicos por tabela e coluna
  const specificMappings: Record<string, Record<string, string>> = {
    'Employee': {
      'name': 'Nome',
      'emailAddress': 'Email',
      'isActive': 'Ativo',
      'createdAt': 'Criado em',
      'updatedAt': 'Atualizado em'
    },
    'Project': {
      'name': 'Nome',
      'description': 'Descrição',
      'startDate': 'Data de Início',
      'endDate': 'Data de Fim',
      'status': 'Status'
    }
  };

  // Mapeamentos genéricos
  const genericMappings: Record<string, string> = {
    'id': 'ID',
    'name': 'Nome',
    'description': 'Descrição',
    'email': 'Email',
    'emailAddress': 'Email',
    'isActive': 'Ativo',
    'createdAt': 'Criado em',
    'updatedAt': 'Atualizado em',
    'deletedAt': 'Excluído em',
    'startDate': 'Data de Início',
    'endDate': 'Data de Fim',
    'status': 'Status',
    'type': 'Tipo',
    'value': 'Valor'
  };

  // Tentar mapeamento específico primeiro
  if (tableName && specificMappings[tableName]?.[columnName]) {
    return specificMappings[tableName][columnName];
  }

  // Usar mapeamento genérico
  return genericMappings[columnName] || columnName;
};

// Função para calcular dias úteis (excluindo fins de semana e feriados)
export const calculateWorkingDays = (
  year: number, 
  month: number, 
  holidayMap: Map<number, number>
): number => {
  let workDays = new Date(year, month, 0).getDate();
  
  for (let day = 1; day <= workDays; day++) {
    const date = new Date(year, month - 1, day);
    const dayOfWeek = date.getDay();
    const hasHoliday = (holidayMap.get(day) ?? 0) > 0;
    
    if (dayOfWeek === 0 || dayOfWeek === 6 || hasHoliday) {
      workDays--;
    }
  }
  
  return workDays;
};

// Função para processar valor de dia na análise de timesheet
export const processDayValue = (
  day: number,
  year: number,
  month: number,
  hoursAllocated: number,
  hoursAbsent: number,
  hoursVacation: number,
  hoursHoliday: number,
  hoursRest: number,
  isSupportService?: boolean
): { value: string | number; isWorkDay: boolean } => {
  const date = new Date(year, month - 1, day);
  const dayOfWeek = date.getDay();

  const isWeekend =
    !isSupportService && (dayOfWeek === 0 || dayOfWeek === 6);

  let value: string | number = 0;
  let isWorkDay = true;

  // 1. FÉRIAS (sempre prioridade máxima)
  if (hoursVacation > 0) {
    value = "F";
    return { value, isWorkDay };
  }
  // 2. AUSÊNCIA do tipo dia de descanso
if (hoursRest > 0) {
  return {
    value: "D",
    isWorkDay: true,
  };
}
  // 2. AUSÊNCIA
  if (hoursAbsent > 0) {
    value = "A";
    return { value, isWorkDay };
  }

  // 3. FERIADO
  if (hoursHoliday > 0) {
    value = "H";
    isWorkDay = false;
    return { value, isWorkDay };
  }

  // 4. FIM DE SEMANA (apenas se não for suporte)
  if (!isSupportService && isWeekend) {
    value = dayOfWeek === 0 ? "D" : "S";
    isWorkDay = false;
    return { value, isWorkDay };
  }

  // 5. HORAS TRABALHADAS
  if (hoursAllocated > 0) {
    value = hoursAllocated;
    return { value, isWorkDay };
  }

  // 6. VAZIO
  return { value: 0, isWorkDay };
};

// Configurações de estilo padrão para PDFs
export const DEFAULT_PDF_STYLES = {
  fontSize: {
    title: 16,
    subtitle: 12,
    header: 10,
    body: 10,
    small: 8
  },
  colors: {
    header: [66, 139, 202] as [number, number, number],
    text: 255,
    background: [245, 245, 245] as [number, number, number]
  },
  margins: {
    default: 10,
    small: 5
  },
  cellPadding: {
    default: 2,
    compact: 1
  }
};